import { ChangeDetectorRef, Component, DestroyRef, inject, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import {
  ButtonComponent,
  HrefDirective,
  IconComponent,
  Lesson,
  MinHeightPanelComponent,
  ModalHeaderButtonsComponent,
  ModalHeaderCloseButtonComponent,
  ModalHeaderComponent,
  ModalHeaderTitleComponent,
  ModalPanelComponent,
  ModalRef,
  ModalService,
  ModalSubHeaderComponent,
  OrderSecurity,
  SubModalRef,
  ToolbarComponent,
  ToolbarItemHelpComponent,
  ToolbarItemPrintComponent,
  TRANSFERS_TABS,
  TransfersHelpArticleId,
  TransfersTab
} from '@tdwb/core';

// eslint-disable-next-line td-wb-rules/cross-lazyload-module-import-rule
import { TradingHelpContextService } from '@tdwb/lazy/trading';

import { LearnRestService } from '../../learn-rest.service';
import { DisplayLearnInformation, LearnLinkIdMapping, TradingLessonsData } from '../../domain';
import { LearnLessonContentComponent } from '../../content/learn-lesson-content.component';
import { TradingLearnRelatedLessonsComponent } from '../trading-learn-related-lessons/trading-learn-related-lessons.component';

@Component({
  selector: 'td-wb-trading-learn-modal',
  templateUrl: './trading-learn-modal.component.html',
  styleUrls: ['./trading-learn-modal.component.scss'],
  exportAs: 'tdWbContextualLearning',
  imports: [
    MinHeightPanelComponent,
    ModalHeaderComponent,
    ModalHeaderTitleComponent,
    ModalHeaderButtonsComponent,
    ModalHeaderCloseButtonComponent,
    ToolbarComponent,
    ToolbarItemPrintComponent,
    ToolbarItemHelpComponent,
    ModalSubHeaderComponent,
    ModalPanelComponent,
    TranslateDirective,
    IconComponent,
    LearnLessonContentComponent,
    TradingLearnRelatedLessonsComponent,
    ButtonComponent,
    HrefDirective
  ]
})
export class TradingLearnModalComponent {
  private readonly learnRestService = inject(LearnRestService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly modalRef = inject(ModalRef<unknown>, { optional: true });
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly contextualLearning = viewChild.required<TemplateRef<unknown>>('contextualLearningTemplate');
  tabId: OrderSecurity | TransfersTab;
  page: string;
  modalTitle: string;
  modalIcon: string;
  modalArticleId: string;
  contextualLesson: Lesson;
  relatedLessons: Array<Lesson>;
  loadingIndicator: boolean = true;
  showSubHeader: boolean = true;

  private modalReference: ModalRef<unknown>;
  private subModalRef: SubModalRef;

  constructor() {}

  openContextualLearningModal(tabId: OrderSecurity | TransfersTab, page: DisplayLearnInformation): void {
    this.tabId = tabId;
    this.getModalInformation(page);
    this.learnRestService.fetchTradingLessons(LearnLinkIdMapping[tabId])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (data: TradingLessonsData): void => {
          this.contextualLesson = data.contextualLesson;
          this.relatedLessons = data.relatedLessons;
          this.loadingIndicator = false;
          if (this.modalRef === undefined || this.modalRef === null) {
            this.modalReference = this.modalService.open(this.contextualLearning(), {
              size: 'medium'
            });
          } else {
            this.subModalRef = this.modalRef.openSubModal(this.contextualLearning());
          }
        },
        error: (): void => {
          this.loadingIndicator = false;
          this.relatedLessons = [];
        }
      });
  }

  closeModal(): void {
    this.modalReference?.close();
    this.subModalRef?.close();
  }

  selectLesson(lesson: Lesson): void {
    this.contextualLesson = lesson;
    this.changeDetectorRef.markForCheck();
  }

  private getArticleIdCreate(): string {
    return TradingHelpContextService.getCreateArticleId(this.tabId ?? '', 'edit');
  }

  getBackButtonForOrigin(): string {
    return `tdWb.tradingLearn.information.${this.tabId}.BACK`;
  }

  getBackLinkForOrigin(): string {
    if (TRANSFERS_TABS.includes(this.tabId as TransfersTab)) {
      return `tdWb.tradingLearn.information.${this.tabId}.BACK_LINK`;
    } else {
      return `tdWb.tradingLearn.information.${this.tabId}.BACK`;
    }
  }

  getModalInformation(page: DisplayLearnInformation): void {
    switch(page) {
      case 'transfers':
        this.modalTitle =  'TRANSFERS_TITLE';
        this.modalIcon = 'tertiary--arrow-leftright';
        if (this.tabId === 'cash') {
          this.modalArticleId = TransfersHelpArticleId.TRAN_ENTER;
        } else if (this.tabId === 'ofi') {
          this.modalArticleId = TransfersHelpArticleId.OFI_TRANSFERS;
        } else {
          this.modalArticleId = TransfersHelpArticleId.SECURITY_TRANSFERS;
        }
        break;
      case 'holdings':
        this.modalTitle =  'HEADER';
        this.modalIcon = undefined;
        this.modalArticleId = 'acct_access';
        this.showSubHeader = false;
        break;
      default:
        this.modalTitle =  'BUYSELL_TITLE';
        this.modalIcon = 'tertiary--arrow-updown';
        this.modalArticleId = this.getArticleIdCreate();
        break;
    }
  }

}
